import { UndoRounded } from "@mui/icons-material";

import { CSSProperties } from "react";
import { FanboxColorName, FanboxColorPalette } from "../Shared/Utility/Color";


export type BorderStyle = 'none' | 'hidden' | 'solid' | 'groove' | 'dotted' | 'dashed' | 'double' | 'ridge' | 'inset' | 'outset';
export type QuadMetricType = 'margin' | 'padding' | 'border';

export type FanboxStyling ={
    width? : number;
    enforceWidthMin? : boolean;
    enforceWidthMax? : boolean;
    height? : number;
    enforceHeightMin? : boolean;
    enforceHeightMax? : boolean;
    boundary? : Boundary;
    font? : FontProperties;
    position? : PositioningProperties;
    color? : ComponentColor;
}


export type QuadMetric = {
    quadType: QuadMetricType;
    top: number;
    bottom: number;
    left: number;
    right: number;
    color?: FanboxColorName;
    borderStyle?: BorderStyle;
}

export type Boundary = {
    margin?: QuadMetric;
    padding?: QuadMetric;
    border?: QuadMetric;
    radius? : number;
}

export type FontWeight = 'light' | 'regular' | 'semiBold' | 'bold' | 'superBold';

export type FontProperties = {
    size : number;
    weight : FontWeight;
}

export type PositioningType = 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky';

export type PositioningProperties = {
    positioningType : PositioningType;
    top? : number;
    left? : number;
    right? : number;
    bottom? : number;
}

export type ComponentColor = {
    foreground? : FanboxColorName,
    background? : FanboxColorName,
    image? : string;
}

export const AddQuadMetricToCSSProperties = (quad : QuadMetric, cssProperties : CSSProperties) => {
    if(quad.quadType === 'margin' || quad.quadType === 'padding'){
        const str = quad.top + 'px ' + quad.right + 'px ' + quad.bottom + 'px ' + quad.left + 'px';
        if(quad.quadType === 'margin')
            cssProperties.margin = str;
        else
           cssProperties.padding = str;
    }
    else {
        const color = quad.color ?? 'white';
        const borderStyle = quad.borderStyle ?? 'solid';
        cssProperties.borderTop = quad.top + 'px ' + borderStyle + ' ' + color;
        cssProperties.borderRight = quad.right + 'px ' + borderStyle + ' ' + color;
        cssProperties.borderBottom = quad.bottom + 'px ' + borderStyle + ' ' + color;
        cssProperties.borderLeft = quad.left + 'px ' + borderStyle + ' ' + color;
    }

}

export const AddBoundaryToCSSProperties = (boundary : Boundary, cssProperties : CSSProperties) => {
    if(boundary.margin !== undefined){
        boundary.margin.quadType = 'margin';
        AddQuadMetricToCSSProperties(boundary.margin, cssProperties);
    }
    if(boundary.padding !== undefined){
        boundary.padding.quadType = 'padding';
        AddQuadMetricToCSSProperties(boundary.padding, cssProperties);
    }
    if(boundary.border !== undefined){
        boundary.border.quadType = 'border';
        AddQuadMetricToCSSProperties(boundary.border, cssProperties);
    }

    if(boundary.radius !== undefined)
        cssProperties.borderRadius = boundary.radius + 'px';
}

export const AddDimensionsToCSSProperties = (cssProperties : CSSProperties, fanbox : FanboxStyling ) => {

    if(fanbox.width !== undefined){
        cssProperties.width = fanbox.width + 'px';
        if(fanbox.enforceWidthMin === undefined || fanbox.enforceWidthMin === true)
            cssProperties.minWidth = fanbox.width + 'px';
        if(fanbox.enforceWidthMax === true)
            cssProperties.maxWidth = fanbox.width + 'px';
    }

    if(fanbox.height !== undefined) {
        cssProperties.height = fanbox.height + 'px';
         if(fanbox.enforceHeightMin === undefined || fanbox.enforceHeightMin === true)
            cssProperties.minHeight = fanbox.height + 'px';
        if(((fanbox.enforceHeightMax === undefined || fanbox.enforceHeightMax === true) && fanbox.enforceWidthMax !== true) || 
        (fanbox.enforceHeightMax === true))
            cssProperties.maxHeight = fanbox.height + 'px';
    }
    
}

export const AddComponentColorToCSSProperties = (color : ComponentColor, cssProperties : CSSProperties) => {
    if(color.foreground !== undefined)
        cssProperties.color = FanboxColorPalette.get(color.foreground);
    if(color.background !== undefined)
        cssProperties.backgroundColor = FanboxColorPalette.get(color.background);
    if(color.image !== undefined){
        cssProperties.backgroundImage = 'url(' + color.image + ')';
        cssProperties.backgroundPosition = 'left top';
        cssProperties.backgroundSize = 'cover';
    }
}

export const AddFontPropertiesToCSSProperties = (font : FontProperties, cssProperties : CSSProperties) => {
    cssProperties.fontFamily = 'roboto';
    cssProperties.fontSize = font.size + 'px';
    cssProperties.fontWeight = font.weight === 'light' ? 200 : (font.weight === 'regular' ? 400 : (font.weight === 'semiBold' ? 550 : (font.weight === 'bold' ? 700 :900)));
}

export const AddPositioningPropertiesToCSSProperties = (positioning : PositioningProperties, cssProperties : CSSProperties) => {
    if(positioning.positioningType !== 'static'){
        cssProperties.position = positioning.positioningType;
        if(positioning.top !== undefined)
            cssProperties.top = positioning.top + 'px';
        if(positioning.left !== undefined)
            cssProperties.left = positioning.top + 'px';
        if(positioning.bottom !== undefined)
            cssProperties.bottom = positioning.top + 'px';
        if(positioning.right !== undefined)
            cssProperties.right = positioning.top + 'px';
    }
}

export const CreateCSSProperties = (styling : FanboxStyling) : CSSProperties => {
    const ret : CSSProperties = {};
    if(styling.width || styling.height)
        AddDimensionsToCSSProperties(ret, styling);
    if(styling.boundary !== undefined)
        AddBoundaryToCSSProperties(styling.boundary, ret);
    if(styling.color !== undefined)
        AddComponentColorToCSSProperties(styling.color, ret);
    if(styling.font !== undefined)
        AddFontPropertiesToCSSProperties(styling.font, ret);
    if(styling.position !== undefined)
        AddPositioningPropertiesToCSSProperties(styling.position, ret);
    return ret;
}